import { T_MongoDoc, T_MongoId } from "../types/_utils.types";

export type History = {
  round_number: number;
  creditor_last_tone?: string | null;
  creditor_last_offer?: string | null;
  debtor_floor?: number;
  debtor_ceiling?: number;
};

export type HardDeadline = {
  label: string;
  date: string;
};

export type ModelOption = "openai" | "google" | "anthropic";

export type Context = {
  objective: string;
  type: string;
  history: History;
  negotiate_as: string;
  paid_to: string;
  stage: string;
  rationale: string[];
  priority: string;
  hard_deadlines: HardDeadline[];
  hardship_flags: string[];
  notes?: string;
  engage_legal_team?: boolean;
  llm_input: ModelOption;
};

export type Tonality = {
  empathy: string;
  rigidity: string;
  politeness: string;
  system_agression?: number;
  emotion_focus: string[];
  formality: string;
  sentence_style: string;
  persuasion_mode: string;
  urgency_level: string;
};

export type StyleGuide = {
  voice: string;
  constraints: Record<string, any>;
  must_include: string[];
  avoid: string[];
};

export type MessageGeneration = {
  style_guide: StyleGuide;
  data_points_to_surface: string[];
};

export type Compliance = {
  no_admissions_of_liability: boolean;
  no_unauthorized_legal_advice: boolean;
  respect_opt_out: boolean;
  include_disclaimers: boolean;
};

export type Sensitivity = {
  avoid_threatening_language: boolean;
  avoid_personal_data_requests: boolean;
};

export type RedlinesAndStoppers = {
  absolute_max_cash_offer_percent: number;
  no_confession_of_judgment: boolean;
  no_additional_collateral: boolean;
  no_personal_guarantee_additions: boolean;
};

export type ConstraintsAndGuardrails = {
  compliance: Compliance;
  sensitivity: Sensitivity;
  redlines_and_stoppers: RedlinesAndStoppers;
};

export type AnchorStrategy = {
  initial_anchor_percent_of_balance?: number;
  justification_bullets: string[];
};

export type RestructuringTerms = {
  max_term_weeks?: number;
  min_down_payment_percent?: number;
  apr_cap?: number;
  grace_period_days: number;
  balloon_allowed?: boolean;
  late_fee_waiver_expected?: boolean;
  interest_waiver_percent_target?: number;
};

export type DelayOption = {
  type: string;
  days: number;
};

export type DelayCriteria = {
  min_dscr: number;
  recent_delinquency_days_max: number;
};

export type DelayPath = {
  eligible: boolean;
  criteria: DelayCriteria;
  options: DelayOption[];
};

export type SettlementBand = {
  floor_percent?: number;
  target_percent?: number;
  ceiling_percent?: number;
  initial_payment?: number;
  weekly_required?: boolean;
};

export type StrategyConfig = {
  anchor_strategy: AnchorStrategy;
  restructuring_terms?: RestructuringTerms;
  delay_path?: DelayPath;
  settlement_band: SettlementBand;
};

export type Config = {
  tonality: Tonality;
  locale: string;
  currency: string;
  message_generation: MessageGeneration;
  constraints_and_guardrails: ConstraintsAndGuardrails;
  strategy_config: StrategyConfig;
};

export type NegotiationDto = {
  context: Context;
  config: Config;
  _id?: string;
};

export enum MessageType {
  GENERATED_RESPONSE = "generated_response",
  CREDITOR_RESPONSE = "creditor_response",
}

export type Conclusion = {
  status: "accepted" | "rejected" | "ongoing" | "stalled" | "not_concluded";
  direction: "positive" | "negative" | "neutral" | "uncertain";
  sentiment: "cooperative" | "firm" | "hostile" | "frustrated" | "optimistic" | "uncertain";
  rounds: number;
  debtor_last_offer: string;
  probable_conclusion: string;
  recommendation: string;
};

export type CommunicationDto = {
  creditor_id: T_MongoId;
  context: Context;
  config: Config;
  conclusion?: Conclusion;
  emails: {
    body: string;
    timestamp: string;
    parent_id?: string;
    channel: "email";
    origin: "system";
    subject: string;
    reason_for_action: string;
    context: Context;
    config: Config;
    _id: string;
  }[];
  messages: {
    body: string;
    timestamp: string;
    parent_id?: string;
    channel: "message";
    origin: "client";
    type: MessageType;
    _id: string;
  }[];
};

export type CommunicationSchemaDto = CommunicationDto & T_MongoDoc;
export type UpdateCommunicationSchemaDto = Partial<CommunicationSchemaDto>;
